import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Separator } from '../components/ui/separator';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { PdfCanvasViewer } from '../components/PdfCanvasViewer';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  BookOpen,
  Microscope,
  Church,
  Image,
  Headphones,
  FileText,
  Quote,
  Sparkles,
  Loader2,
  ExternalLink,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL, API_URL } from '../lib/api';

const API = API_URL;

const baseSections = [
  { id: 'audio', icon: Headphones, label: 'Áudio' },
  { id: 'pdf', icon: FileText, labelKey: 'documents' },
  { id: 'overview', icon: BookOpen, labelKey: 'historicalContext' },
  { id: 'phenomenon', icon: Sparkles, labelKey: 'phenomenon' },
  { id: 'timeline', icon: Calendar, labelKey: 'timeline' },
  { id: 'scientific', icon: Microscope, labelKey: 'scientificEvidence' },
  { id: 'verdict', icon: Church, labelKey: 'churchVerdict' },
  { id: 'media', icon: Image, labelKey: 'media' },
  { id: 'references', icon: FileText, labelKey: 'references' },
];

export const MiracleDetail = () => {
  const { id } = useParams();
  const { language, t } = useLanguage();
  const [miracle, setMiracle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(null);
  const [audioDiagnostics, setAudioDiagnostics] = useState({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setActiveSection('overview');
    fetchMiracle();
  }, [id]);

  useEffect(() => {
    setAudioDiagnostics({});
  }, [id]);

  const fetchMiracle = async () => {
    try {
      const response = await axios.get(`${API}/miracles/${id}`);
      setMiracle(response.data);
    } catch (error) {
      console.error('Error fetching miracle:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const getTranslated = (field) => {
    if (language !== 'pt' && miracle?.translations?.[language]?.[field]) {
      return miracle.translations[language][field];
    }
    return miracle?.[field];
  };


  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
    return match?.[1] ? `https://www.youtube.com/embed/${match[1]}` : null;
  };


  const resolveMediaUrl = (url, type = '') => {
    if (!url || typeof url !== 'string') return '';

    const trimmedUrl = url.trim();
    if (!trimmedUrl) return '';

    if (/^(https?:|data:|blob:)/i.test(trimmedUrl)) return trimmedUrl;
    if (trimmedUrl.startsWith('//')) return `https:${trimmedUrl}`;

    if (trimmedUrl.startsWith('/')) {
      return `${API_BASE_URL}${trimmedUrl}`;
    }

    if (trimmedUrl.startsWith('api/')) {
      return `${API_BASE_URL}/${trimmedUrl}`;
    }

    if (/^[a-f\d]{24}(\.[a-z0-9]+)?$/i.test(trimmedUrl)) {
      return `${API}/uploads/${trimmedUrl}`;
    }

    if (/^uploads\//i.test(trimmedUrl)) {
      return `${API}/${trimmedUrl}`;
    }

    if (!trimmedUrl.includes('/') && ['audio', 'pdf', 'image', 'video'].includes(type)) {
      return `${API}/uploads/${encodeURIComponent(trimmedUrl)}`;
    }

    return trimmedUrl;
  };

  const getAudioMimeType = (url) => {
    if (!url) return null;
    const cleanUrl = url.split('?')[0].toLowerCase();
    if (cleanUrl.endsWith('.mp3')) return 'audio/mpeg';
    if (cleanUrl.endsWith('.ogg')) return 'audio/ogg';
    if (cleanUrl.endsWith('.wav')) return 'audio/wav';
    if (cleanUrl.endsWith('.m4a')) return 'audio/mp4';
    if (cleanUrl.endsWith('.aac')) return 'audio/aac';
    if (cleanUrl.endsWith('.webm')) return 'audio/webm';
    return null;
  };

  const updateAudioDiagnostic = (audioKey, partialState) => {
    setAudioDiagnostics((prev) => ({
      ...prev,
      [audioKey]: {
        ...(prev[audioKey] || {}),
        ...partialState,
      },
    }));
  };

  const handleAudioLoadedMetadata = (audioKey, event) => {
    const durationSeconds = event?.currentTarget?.duration;
    const isDurationValid = Number.isFinite(durationSeconds) && durationSeconds > 0;

    updateAudioDiagnostic(audioKey, {
      status: 'ready',
      detail: isDurationValid
        ? `Duração detectada: ${durationSeconds.toFixed(1)}s`
        : 'Metadados carregados. Alguns dispositivos móveis podem não informar a duração, mesmo com reprodução normal.',
    });
  };

  const handleAudioCanPlay = (audioKey) => {
    updateAudioDiagnostic(audioKey, {
      status: 'ready',
      detail: 'Áudio pronto para reprodução.',
    });
  };

  const handleAudioError = (audioKey, mediaUrl, event) => {
    const mediaErrorCode = event?.currentTarget?.error?.code;
    const codeMap = {
      1: 'MEDIA_ERR_ABORTED (carregamento interrompido)',
      2: 'MEDIA_ERR_NETWORK (falha de rede)',
      3: 'MEDIA_ERR_DECODE (erro de decodificação)',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED (URL/formato não suportado)'
    };

    updateAudioDiagnostic(audioKey, {
      status: 'error',
      detail: `Falha ao carregar áudio (${codeMap[mediaErrorCode] || 'erro desconhecido'}). URL: ${mediaUrl}`,
    });

    probeAudioEndpoint(mediaUrl).then((probe) => {
      if (!probe) return;

      updateAudioDiagnostic(audioKey, {
        status: probe.ok ? 'warning' : 'error',
        detail: `Falha ao carregar áudio (${codeMap[mediaErrorCode] || 'erro desconhecido'}). ${probe.detail}. URL: ${mediaUrl}`,
      });
    });
  };

  const probeAudioEndpoint = async (mediaUrl) => {
    if (!mediaUrl) return null;

    try {
      const response = await fetch(mediaUrl, {
        method: 'GET',
        headers: {
          Range: 'bytes=0-1',
        },
      });

      const contentType = response.headers.get('content-type');
      const detailParts = [`HTTP ${response.status}`];
      if (contentType) detailParts.push(`content-type: ${contentType}`);

      return {
        ok: response.ok,
        detail: detailParts.join(' · '),
      };
    } catch (error) {
      return {
        ok: false,
        detail: 'Não foi possível validar a URL via fetch (possível bloqueio de CORS/rede)',
      };
    }
  };


  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const imageMedia = miracle?.media?.filter(item => item.type === 'image') || [];
  const videos = miracle?.media?.filter(item => item.type === 'video' || item.type === 'youtube') || [];
  const audios = miracle?.media?.filter(item => item.type === 'audio') || [];
  const pdfs = miracle?.media?.filter(item => item.type === 'pdf') || [];
  const sections = baseSections.filter((section) => {
    if (section.id === 'audio') return audios.length > 0;
    if (section.id === 'pdf') return pdfs.length > 0;
    return true;
  });
  const currentSectionIndex = sections.findIndex(s => s.id === activeSection);
  const prevSection = sections[currentSectionIndex - 1];
  const nextSection = sections[currentSectionIndex + 1];

  useEffect(() => {
    const sectionElements = sections
      .map(({ id: sectionId }) => document.getElementById(sectionId))
      .filter(Boolean);

    if (sectionElements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSections = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleSections.length > 0) {
          setActiveSection(visibleSections[0].target.id);
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -60% 0px',
        threshold: [0.1, 0.25, 0.4, 0.6],
      }
    );

    sectionElements.forEach((sectionElement) => observer.observe(sectionElement));

    return () => observer.disconnect();
  }, [sections]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!miracle) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <p className="text-[#A1A1AA]">{t('noResults')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" data-testid="miracle-detail-page">
      {/* Back button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/"
          className="inline-flex items-center text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          <span className="uppercase text-xs tracking-widest">{t('home')}</span>
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24">
              <nav className="space-y-1" data-testid="section-nav">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`section-nav-item w-full text-left flex items-center gap-3 ${
                      activeSection === section.id ? 'active' : ''
                    }`}
                    data-testid={`nav-${section.id}`}
                  >
                    <section.icon className="w-4 h-4" />
                    {section.labelKey ? t(section.labelKey) : section.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3 space-y-12">
            {/* Header */}
            <header className="animate-fade-in-up">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-4xl">{miracle.country_flag}</span>
                <span
                  className={`text-xs uppercase tracking-wider px-3 py-1 ${
                    miracle.status === 'recognized' ? 'badge-recognized' : 'badge-investigating'
                  }`}
                >
                  {miracle.status === 'recognized' ? t('statusRecognized') : t('statusInvestigating')}
                </span>
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#E5E5E5] mb-4">
                {getTranslated('name')}
              </h1>

              {imageMedia.length > 0 && (
                <div className="mb-6" data-testid="header-image-gallery">
                  <div className="flex flex-wrap gap-3">
                    {imageMedia.map((item, index) => (
                      <button
                        type="button"
                        key={`${item.url}-${index}`}
                        className="group block"
                        title={item.title || getTranslated('name')}
                        onClick={() => setSelectedImage(item)}
                      >
                        <img
                          src={item.url}
                          alt={item.title || getTranslated('name')}
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover border border-[#27272A] group-hover:border-[#D4AF37] transition-colors"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-6 text-[#A1A1AA]">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span>{miracle.city}, {miracle.country}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  <span>{t('century')} {miracle.century} {miracle.year && `(${miracle.year})`}</span>
                </div>
              </div>
            </header>

            {audios.length > 0 && (
              <section id="audio" className="scroll-mt-24 animate-fade-in-up" data-testid="section-audio">
                <div className="flex items-center gap-3 mb-6">
                  <Headphones className="w-5 h-5 text-[#D4AF37]" />
                  <h2 className="font-serif text-2xl text-[#E5E5E5]">Áudio</h2>
                </div>

                <div className="bg-[#121214] border border-[#27272A] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm" role="img" aria-label="Português do Brasil">🇧🇷</span>
                    <h3 className="text-[#E5E5E5] font-serif text-lg">Áudio do resumo (PT-BR)</h3>
                  </div>
                  <p className="text-[#A1A1AA] text-xs mb-4">No momento, o áudio está disponível apenas em português do Brasil.</p>
                  <div className="space-y-3">
                    {audios.map((item, index) => {
                      const mediaUrl = resolveMediaUrl(item.url, 'audio');
                      const audioMimeType = getAudioMimeType(mediaUrl);
                      const audioKey = `${item.url}-summary-${index}`;
                      const diagnostic = audioDiagnostics[audioKey];

                      return (
                        <div key={audioKey} className="border border-[#27272A] bg-[#0A0A0B] p-3">
                          <audio
                            controls
                            playsInline
                            preload="metadata"
                            className="w-full mb-2"
                            onLoadedMetadata={(event) => handleAudioLoadedMetadata(audioKey, event)}
                            onCanPlay={() => handleAudioCanPlay(audioKey)}
                            onError={(event) => handleAudioError(audioKey, mediaUrl, event)}
                          >
                            {audioMimeType && (
                              <source src={mediaUrl} type={audioMimeType} />
                            )}
                            {!audioMimeType && <source src={mediaUrl} />}
                            Seu navegador não suporta áudio.
                          </audio>
                          {diagnostic?.detail && (
                            <p
                              className={`text-xs mb-2 ${
                                diagnostic.status === 'error'
                                  ? 'text-red-400'
                                  : diagnostic.status === 'warning'
                                    ? 'text-amber-300'
                                    : 'text-emerald-400'
                              }`}
                            >
                              {diagnostic.detail}
                            </p>
                          )}
                          <a
                            href={mediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#D4AF37] text-xs mb-2 hover:underline"
                          >
                            Abrir áudio em nova aba <ExternalLink className="w-3 h-3" />
                          </a>
                          <h4 className="text-[#E5E5E5] text-sm font-medium">{item.title || 'Áudio'}</h4>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {audios.length > 0 && <Separator className="bg-[#27272A]" />}

            {pdfs.length > 0 && (
              <section id="pdf" className="scroll-mt-24 animate-fade-in-up" data-testid="section-pdf">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-5 h-5 text-[#D4AF37]" />
                  <h2 className="font-serif text-2xl text-[#E5E5E5]">Documentos (PDF)</h2>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {pdfs.map((item, index) => {
                    const pdfKey = `${item.url}-${index}`;
                    const mediaUrl = resolveMediaUrl(item.url, 'pdf');

                    return (
                      <div key={pdfKey} className="bg-[#121214] border border-[#27272A] p-4">
                        <h4 className="text-[#E5E5E5] font-medium">{item.title || 'Documento PDF'}</h4>
                        {item.description && (
                          <p className="text-[#A1A1AA] text-sm mt-1">{item.description}</p>
                        )}

                        <div className="mt-4">
                          <PdfCanvasViewer
                            url={mediaUrl}
                            title={item.title || `Documento PDF ${index + 1}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {pdfs.length > 0 && <Separator className="bg-[#27272A]" />}

            {(miracle.summary || getTranslated('summary')) && (
              <section className="bg-[#121214] border border-[rgba(212,175,55,0.3)] p-6 animate-fade-in-up" data-testid="summary-section">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  <h3 className="font-serif text-lg text-[#D4AF37]">{t('summary')}</h3>
                </div>
                <p className="text-[#E5E5E5] leading-relaxed whitespace-pre-wrap">
                  {getTranslated('summary') || miracle.summary}
                </p>
              </section>
            )}

            {/* Overview Section */}
            <section id="overview" className="scroll-mt-24 animate-fade-in-up" data-testid="section-overview">
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-serif text-2xl text-[#E5E5E5]">{t('historicalContext')}</h2>
              </div>
              <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-wrap">
                {getTranslated('historical_context')}
              </p>
            </section>

            <Separator className="bg-[#27272A]" />

            {/* Phenomenon Section */}
            <section id="phenomenon" className="scroll-mt-24 animate-fade-in-up" data-testid="section-phenomenon">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-serif text-2xl text-[#E5E5E5]">{t('phenomenon')}</h2>
              </div>
              <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-wrap">
                {getTranslated('phenomenon_description')}
              </p>
            </section>

            <Separator className="bg-[#27272A]" />

            {/* Timeline Section */}
            <section id="timeline" className="scroll-mt-24 animate-fade-in-up" data-testid="section-timeline">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-serif text-2xl text-[#E5E5E5]">{t('timeline')}</h2>
              </div>
              
              {miracle.timeline?.length > 0 ? (
                <div className="relative pl-8">
                  <div className="timeline-line absolute left-[5px] top-2 bottom-2 w-[2px]" />
                  <div className="space-y-8">
                    {miracle.timeline.map((event, index) => (
                      <div key={index} className="relative">
                        <div className="timeline-node absolute -left-8 top-1" />
                        <div className="text-[#D4AF37] font-serif text-lg mb-2">{event.year}</div>
                        <h4 className="text-[#E5E5E5] font-medium mb-1">{event.title}</h4>
                        <p className="text-[#A1A1AA] text-sm">{event.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[#52525B] italic">{t('noResults')}</p>
              )}
            </section>

            <Separator className="bg-[#27272A]" />

            {/* Scientific Evidence Section */}
            <section id="scientific" className="scroll-mt-24 animate-fade-in-up" data-testid="section-scientific">
              <div className="flex items-center gap-3 mb-6">
                <Microscope className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-serif text-2xl text-[#E5E5E5]">{t('scientificEvidence')}</h2>
              </div>
              
              {miracle.scientific_reports?.length > 0 ? (
                <div className="space-y-6">
                  {miracle.scientific_reports.map((report, index) => (
                    <div key={index} className="bg-[#121214] border border-[#27272A] p-6">
                      <div className="text-[#D4AF37] text-sm uppercase tracking-wider mb-3">{report.date}</div>
                      <p className="text-[#E5E5E5] mb-4">{report.description}</p>
                      
                      {report.experts?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Especialistas</h4>
                          <div className="space-y-1">
                            {report.experts.map((expert, i) => (
                              <div key={i} className="text-[#E5E5E5] text-sm">
                                {expert.name} - <span className="text-[#A1A1AA]">{expert.institution}</span>
                                {expert.role && <span className="text-[#52525B]"> ({expert.role})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {report.original_excerpts?.length > 0 && (
                        <div>
                          <h4 className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Trechos Originais</h4>
                          {report.original_excerpts.map((excerpt, i) => (
                            <blockquote key={i} className="border-l-2 border-[#D4AF37] pl-4 italic text-[#A1A1AA] text-sm">
                              <Quote className="w-4 h-4 text-[#D4AF37] mb-1" />
                              {excerpt}
                            </blockquote>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#52525B] italic">{t('noResults')}</p>
              )}
            </section>

            <Separator className="bg-[#27272A]" />

            {/* Church Verdict Section */}
            <section id="verdict" className="scroll-mt-24 animate-fade-in-up" data-testid="section-verdict">
              <div className="flex items-center gap-3 mb-6">
                <Church className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-serif text-2xl text-[#E5E5E5]">{t('churchVerdict')}</h2>
              </div>
              <div className="bg-[#121214] border border-[rgba(212,175,55,0.3)] p-6">
                <p className="text-[#E5E5E5] leading-relaxed whitespace-pre-wrap">
                  {getTranslated('church_verdict')}
                </p>
              </div>
            </section>

            <Separator className="bg-[#27272A]" />

            {/* Media Section */}
            <section id="media" className="scroll-mt-24 animate-fade-in-up" data-testid="section-media">
              <div className="flex items-center gap-3 mb-6">
                <Image className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-serif text-2xl text-[#E5E5E5]">{t('media')}</h2>
              </div>
              
              {videos.length > 0 ? (
                <div className="space-y-8">
                  {videos.length > 0 && (
                    <div>
                      <h3 className="text-[#E5E5E5] font-serif text-xl mb-4">Vídeos</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {videos.map((item, index) => (
                          <div key={`${item.url}-${index}`} className="bg-[#121214] border border-[#27272A] p-4">
                            {item.type === 'youtube' && getYoutubeEmbedUrl(item.url) ? (
                              <iframe
                                src={getYoutubeEmbedUrl(item.url)}
                                title={item.title || getTranslated('name')}
                                className="w-full h-48 mb-3"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                              />
                            ) : (
                              <video controls className="w-full h-48 object-cover mb-3 bg-[#0A0A0B]">
                                <source src={item.url} />
                                Seu navegador não suporta vídeo.
                              </video>
                            )}
                            <h4 className="text-[#E5E5E5] font-medium">{item.title}</h4>
                            {item.description && (
                              <p className="text-[#A1A1AA] text-sm mt-1">{item.description}</p>
                            )}
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[#D4AF37] text-xs mt-2 hover:underline"
                            >
                              Abrir mídia <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              ) : (
                <p className="text-[#52525B] italic">{t('noResults')}</p>
              )}
            </section>

            <Separator className="bg-[#27272A]" />

            {/* References Section */}
            <section id="references" className="scroll-mt-24 animate-fade-in-up" data-testid="section-references">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-serif text-2xl text-[#E5E5E5]">{t('references')}</h2>
              </div>
              
              {miracle.references?.length > 0 ? (
                <ul className="space-y-4">
                  {miracle.references.map((ref, index) => (
                    <li key={index} className="text-[#A1A1AA] text-sm pl-4 border-l border-[#27272A]">
                      {ref.citation}
                      {ref.url && (
                        <a
                          href={ref.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-[#D4AF37] hover:underline"
                        >
                          <ExternalLink className="w-3 h-3 inline" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[#52525B] italic">{t('noResults')}</p>
              )}
            </section>

            {/* Section Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-[#27272A]">
              {prevSection ? (
                <button
                  onClick={() => scrollToSection(prevSection.id)}
                  className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                  data-testid="prev-section-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="text-sm uppercase tracking-wider">{prevSection.labelKey ? t(prevSection.labelKey) : prevSection.label}</span>
                </button>
              ) : <div />}
              
              {nextSection && (
                <button
                  onClick={() => scrollToSection(nextSection.id)}
                  className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                  data-testid="next-section-btn"
                >
                  <span className="text-sm uppercase tracking-wider">{nextSection.labelKey ? t(nextSection.labelKey) : nextSection.label}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </main>
        </div>
      </div>

      <Dialog open={Boolean(selectedImage)} onOpenChange={(open) => !open && setSelectedImage(null)}>
        <DialogContent className="!w-auto max-w-[95vw] bg-[#121214] border-[#27272A] p-2 sm:p-4">
          <DialogTitle className="sr-only">
            {selectedImage?.title || getTranslated('name')}
          </DialogTitle>
          {selectedImage && (
            <img
              src={selectedImage.url}
              alt={selectedImage.title || getTranslated('name')}
              className="w-auto h-auto max-w-full max-h-[85vh] object-contain mx-auto"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
